// ── Step 6 Suggestion Cards — Pure DOM writes, no state or API ────────────

import {
  formatRevisionFieldLabel,
  normalizeRevisionSeverityClass,
  formatRevisionSeverityLabel,
} from '../../../revision-display.js';

function createSuggestionElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createSeverityBadge(severity) {
  return createSuggestionElement(
    'span',
    `revision-severity ${normalizeRevisionSeverityClass(severity)}`,
    formatRevisionSeverityLabel(severity),
  );
}

function createReviewNotice(message) {
  if (!message) return null;
  const notice = createSuggestionElement('div', 'revision-review-notice');
  notice.setAttribute('role', 'status');
  notice.append(
    createSuggestionElement('strong', '', '全文审稿未完成'),
    createSuggestionElement('span', '', message),
  );
  return notice;
}

function createReviewBlock(label, contentClass, text) {
  const block = createSuggestionElement('div', 'revision-review-block');
  block.append(
    createSuggestionElement('div', 'revision-review-label', label),
    createSuggestionElement('div', contentClass, text),
  );
  return block;
}

function createOverviewCard(overview) {
  if (!overview) return null;
  const card = createSuggestionElement('div', 'revision-overview-card');
  const copy = createSuggestionElement('div');
  copy.append(
    createSuggestionElement('div', 'revision-overview-title', '改稿总览'),
    createSuggestionElement(
      'div',
      'revision-overview-subtitle',
      overview.summaryText || (overview.status === 'needs_revision' ? '当前 Listing 需要调整' : '当前 Listing 基本可放行'),
    ),
  );
  if (overview.auditNote) copy.append(createSuggestionElement('div', 'revision-overview-note', overview.auditNote));

  const badges = createSuggestionElement('div', 'revision-overview-badges');
  badges.append(
    createSuggestionElement('span', 'revision-overview-badge severity-high', `严重 ${overview.severityCounts?.critical || 0}`),
    createSuggestionElement('span', 'revision-overview-badge severity-medium', `警告 ${overview.severityCounts?.warning || 0}`),
    createSuggestionElement('span', 'revision-overview-badge severity-low', `提示 ${overview.severityCounts?.info || 0}`),
  );
  const head = createSuggestionElement('div', 'revision-overview-head');
  head.append(copy, badges);
  card.append(head);

  const fields = Object.entries(overview.fieldCounts || {});
  const grid = createSuggestionElement('div', 'revision-overview-grid');
  grid.append(...fields.map(([field, count]) => {
    const chip = createSuggestionElement('div', 'revision-overview-chip');
    chip.append(
      createSuggestionElement('span', '', formatRevisionFieldLabel(field)),
      createSuggestionElement('strong', '', count),
    );
    return chip;
  }));
  card.append(grid);

  if (overview.focusSummary) {
    const focus = createSuggestionElement('div', 'revision-overview-focus');
    focus.append(
      createSuggestionElement('div', 'revision-review-label', '我会先处理'),
      createSuggestionElement('div', 'revision-overview-focus-text', overview.focusSummary),
    );
    card.append(focus);
  }

  const risks = Array.isArray(overview.topRisks) ? overview.topRisks : [];
  if (risks.length) {
    const root = createSuggestionElement('div', 'revision-overview-risks');
    root.append(...risks.map((risk) => {
      const item = createSuggestionElement('div', 'revision-overview-risk');
      const head = createSuggestionElement('div', 'revision-overview-risk-head');
      head.append(
        createSuggestionElement('span', 'revision-field-tag', risk.field ?? ''),
        createSeverityBadge(risk.severity),
      );
      if (risk.itemCount > 1) head.append(createSuggestionElement('span', 'revision-field-tag', `共${risk.itemCount}项`));
      item.append(
        head,
        createSuggestionElement('div', 'revision-overview-risk-issue', risk.issue ?? ''),
        createSuggestionElement('div', 'revision-overview-risk-action', risk.recommendedAction ?? ''),
      );
      return item;
    }));
    card.append(root);
  }

  const prioritySteps = Array.isArray(overview.prioritySteps) ? overview.prioritySteps : [];
  if (prioritySteps.length) {
    const priority = createSuggestionElement('div', 'revision-overview-priority');
    const list = createSuggestionElement('ol', 'revision-overview-priority-list');
    list.append(...prioritySteps.map((step) => {
      const item = createSuggestionElement('li');
      item.append(
        createSuggestionElement('span', 'revision-overview-priority-field', step.field ?? ''),
        createSuggestionElement('span', 'revision-overview-priority-issue', step.issue ?? ''),
      );
      return item;
    }));
    priority.append(createSuggestionElement('div', 'revision-review-label', '优先处理顺序'), list);
    card.append(priority);
  }
  return card;
}

function createTextList(lines, { itemClassName = '', includeActions = false } = {}) {
  const list = createSuggestionElement('ul', itemClassName);
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const item = createSuggestionElement('li');
    if (includeActions) {
      item.append(createSuggestionElement('strong', '', line?.issue || ''));
      const action = line?.recommendedAction || line?.suggestion || '';
      if (action) item.append(document.createElement('br'), document.createTextNode(action));
    } else {
      item.textContent = line ?? '';
    }
    list.append(item);
  });
  return list;
}

function createSuggestionCard(s = {}) {
  const card = createSuggestionElement('div', `revision-item-card${s.editedSuggestion !== s.suggestion ? ' has-edits' : ''}`);
  card.dataset.id = s.id ?? '';
  const itemCount = Number(s.itemCount) || 0;
  const header = createSuggestionElement('div', 'revision-item-header');
  header.append(
    createSuggestionElement('span', 'revision-field-tag', formatRevisionFieldLabel(s.field)),
    createSeverityBadge(s.severity),
  );
  if (itemCount > 1) {
    header.append(createSuggestionElement('span', 'revision-field-tag', `共${itemCount}项`));
    const expand = createSuggestionElement('button', 'ghost-button revision-expand-toggle', '展开细项');
    expand.type = 'button';
    expand.dataset.id = s.id ?? '';
    header.append(expand);
  }
  card.append(header);
  card.append(createReviewBlock('当前情况', 'revision-issue', s.issue ?? ''));
  if (s.reasonSummary) card.append(createReviewBlock('为什么要改', 'revision-review-summary', s.reasonSummary));
  if (s.impactSummary) card.append(createReviewBlock('会带来什么影响', 'revision-review-summary', s.impactSummary));

  const actionBlock = createSuggestionElement('div', 'revision-review-block');
  const action = createSuggestionElement('div', 'revision-group-summary', s.editedSuggestion || s.suggestion || s.recommendedAction || '');
  const adoptHint = String(s.editedSuggestion || s.suggestion || s.recommendedAction || '').trim();
  if (s.field === 'searchTerms' && adoptHint) {
    const adopt = createSuggestionElement('button', 'ghost-button revision-adopt-keyword', '采纳此词');
    adopt.type = 'button';
    adopt.dataset.keyword = adoptHint;
    action.append(adopt);
  }
  actionBlock.append(createSuggestionElement('div', 'revision-review-label', '我会这样改'), action);
  card.append(actionBlock);

  const evidence = Array.isArray(s.evidenceSummary) ? s.evidenceSummary : [];
  if (evidence.length) {
    const evidenceBlock = createSuggestionElement('div', 'revision-review-block');
    const summary = createSuggestionElement('div', 'revision-review-summary');
    summary.append(createTextList(evidence, { itemClassName: 'revision-card-list' }));
    evidenceBlock.append(createSuggestionElement('div', 'revision-review-label', '依据'), summary);
    card.append(evidenceBlock);
  }

  const body = createSuggestionElement('div', `revision-card-body${itemCount > 1 ? ' is-collapsed' : ''}`);
  body.dataset.bodyId = s.id ?? '';
  const details = Array.isArray(s.items) ? s.items : [];
  if (details.length > 1) {
    const block = createSuggestionElement('div', 'revision-original-block');
    const text = createSuggestionElement('div', 'revision-original-text');
    text.append(createTextList(details, { itemClassName: 'revision-card-list', includeActions: true }));
    block.append(createSuggestionElement('div', 'revision-original-label', '细项'), text);
    body.append(block);
  }
  if (s.original) {
    const block = createSuggestionElement('div', 'revision-original-block');
    block.append(
      createSuggestionElement('div', 'revision-original-label', '原文'),
      createSuggestionElement('div', 'revision-original-text', s.original),
    );
    body.append(block);
  }
  card.append(body);
  return card;
}

/** Renders overview and suggestion cards only; their event handlers remain in Step 6. */
export function renderRevisionSuggestionCardsInto(root, { overview = null, suggestions = [], notice = '' } = {}) {
  if (!root || typeof document === 'undefined' || typeof document.createElement !== 'function') return;
  const nodes = [];
  const reviewNotice = createReviewNotice(notice);
  if (reviewNotice) nodes.push(reviewNotice);
  const overviewCard = createOverviewCard(overview);
  if (overviewCard) nodes.push(overviewCard);
  nodes.push(...(Array.isArray(suggestions) ? suggestions : []).map(createSuggestionCard));
  root.replaceChildren(...nodes);
}
